import {
  ARENA_RADIUS,
  CHAIN_WINDOW_TICKS,
  KNOCKBACK_FRICTION,
  KNOCKBACK_MIN_SPEED,
  RECALL_TICKS,
  SPAWN_PROTECTION_TICKS,
  TICK_DT,
  TICK_RATE,
  KILL_CREDIT_TICKS,
} from './constants.ts';
import {
  abilityCharges,
  abilityCooldown,
  abilityDuration,
  HERO_ORDER,
  getHeroDef,
  type AbilityDef,
  type BlinkSpec,
  type ExecuteSpec,
  type GrabSpec,
  type HeroDef,
  type ProjectileSpec,
  type StrikeDef,
} from './heroes.ts';
import {
  add,
  angleOf,
  angleTo,
  cloneVec2,
  distance,
  distanceSq,
  dot,
  fromAngle,
  length,
  lengthSq,
  normalize,
  scale,
  sub,
  vec2,
  withinArc,
  type Vec2,
} from './math.ts';
import {
  GOLD,
  MINION_STATS,
  MINION_WAVE,
  STRUCTURE_LAYOUT,
  STRUCTURE_STATS,
  TOWER,
  heroSpawn,
  laneWaypoints,
  pointAlong,
  respawnTicksFor,
} from './lane.ts';
import {
  BOT_RANK_PRIORITY,
  MAX_LEVEL,
  XP,
  abilityCooldownScale,
  abilityHasteMultiplier,
  abilityPower,
  armorMultiplier,
  attackDamageRatio,
  canRankUp,
  controlDuration,
  effectiveArmor,
  heroStats,
  xpToNext,
} from './progression.ts';
import { BOT_BUILDS, ITEMS, SHOP, getItem, sellPrice } from './items.ts';
import {
  emptyCooldowns,
  emptyInput,
  idleAction,
  type AbilitySlot,
  type ActionState,
  type Cooldowns,
  type HeroId,
  type HeroState,
  type InputState,
  type MatchState,
  type MinionKind,
  type MinionState,
  type ProjectileKind,
  type ProjectileState,
  type SimEvent,
  type StructureState,
  type Team,
} from './types.ts';

/** Distancia de `p` al segmento [a, b]. */
function segmentDistance(p: Vec2, a: Vec2, b: Vec2): number {
  const abx = b.x - a.x;
  const abz = b.z - a.z;
  const len2 = abx * abx + abz * abz;
  const t = len2 > 1e-9 ? Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.z - a.z) * abz) / len2)) : 0;
  return Math.hypot(p.x - (a.x + abx * t), p.z - (a.z + abz * t));
}

/** Rol para la build de objetos de los bots. */
function botRole(heroId: HeroId): string {
  switch (heroId) {
    case 'barbarian':
      return 'tank';
    case 'falco':
    case 'mage':
      return 'marksman';
    case 'sheik':
      return 'assassin';
    default:
      return 'fighter';
  }
}

/** Cualquier cosa que puede recibir daño y ser objetivo. */
export type Unit = HeroState | MinionState | StructureState;

export function isHeroUnit(unit: Unit): unit is HeroState {
  return 'heroId' in unit;
}

export function isStructureUnit(unit: Unit): unit is StructureState {
  return 'tier' in unit;
}

export function isMinionUnit(unit: Unit): unit is MinionState {
  return !isHeroUnit(unit) && !isStructureUnit(unit);
}

/** Héroes junto a su propio nexo recuperan vida (fracción de vida máxima por segundo). */
export const NEXUS_HEAL_RADIUS = 6.5;
const NEXUS_HEAL_PER_SECOND = 0.06;
/** Ticks sin recibir daño antes de que empiece la regeneración de vida base. */
const OUT_OF_COMBAT_REGEN_TICKS = 3 * TICK_RATE;
/** Ventana en la que una habilidad pulsada durante otra acción sigue pendiente. */
const ABILITY_BUFFER_TICKS = 12;

export interface AddHeroOptions {
  team: Team;
  x: number;
  z: number;
  isPlayer?: boolean;
  isBot?: boolean;
  isDummy?: boolean;
  name?: string;
  facing?: number;
  lane?: number;
}

export interface MatchOptions {
  durationTicks: number;
  scoreToWin: number;
}

const ABILITY_SLOTS: AbilitySlot[] = ['q', 'w', 'e', 'r'];
/** distancia a `InputState.moveTo` a la que se da por llegado (más que un paso de tick) */
const MOVE_ARRIVE_RADIUS = 0.3;

/** Cargas iniciales de cada habilidad del héroe (llenas). */
function fullCharges(heroId: HeroId): Cooldowns {
  const abilities = getHeroDef(heroId).abilities;
  return {
    q: abilityCharges(abilities.q),
    w: abilityCharges(abilities.w),
    e: abilityCharges(abilities.e),
    r: abilityCharges(abilities.r),
  };
}

export class Simulation {
  tick = 0;
  heroes: HeroState[] = [];
  projectiles: ProjectileState[] = [];
  events: SimEvent[] = [];
  match: MatchState = {
    enabled: false,
    durationTicks: 0,
    scoreToWin: 0,
    elapsedTicks: 0,
    score: { blue: 0, red: 0 },
    winner: null,
    ended: false,
  };

  structures: StructureState[] = [];
  minions: MinionState[] = [];
  laneEnabled = false;
  /** líneas en juego (3v3: las tres; ARAM: sólo la central) */
  activeLanes: number[] = [0, 1, 2];
  nextWaveTick = 0;
  /** Con progresión los héroes empiezan en nivel 1 con todo bloqueado. */
  progression = false;

  private nextHeroId = 1;
  private nextProjectileId = 1;
  private nextUnitId = 1000;

  get player(): HeroState | undefined {
    return this.heroes.find((hero) => hero.isPlayer);
  }

  enableMatch(options: MatchOptions): void {
    this.match = {
      enabled: true,
      durationTicks: options.durationTicks,
      scoreToWin: options.scoreToWin,
      elapsedTicks: 0,
      score: { blue: 0, red: 0 },
      winner: null,
      ended: false,
    };
  }

  /** Activa las líneas: torres, nexos y oleadas de minions (por defecto las tres). */
  enableLane(lanes: number[] = [0, 1, 2]): void {
    this.laneEnabled = true;
    this.activeLanes = [...lanes];
    const specs = STRUCTURE_LAYOUT.filter((spec) => spec.lane === -1 || lanes.includes(spec.lane));
    this.structures = specs.map((spec) => {
      const stats = STRUCTURE_STATS[spec.kind];
      return {
        id: this.nextUnitId++,
        kind: spec.kind,
        team: spec.team,
        tier: spec.tier,
        lane: spec.lane,
        model: spec.model,
        pos: vec2(spec.x, spec.z),
        radius: stats.radius,
        hp: stats.maxHp,
        maxHp: stats.maxHp,
        alive: true,
        cooldown: 0,
        targetId: 0,
        // mirando hacia el centro de la línea
        facing: spec.team === 'blue' ? 0 : Math.PI,
      };
    });
    this.nextWaveTick = this.tick + MINION_WAVE.firstAtTicks;
  }

  addMinion(kind: MinionKind, team: Team, x: number, z: number, lane = 1): MinionState {
    const stats = MINION_STATS[kind];
    const minion: MinionState = {
      id: this.nextUnitId++,
      kind,
      team,
      lane,
      waypoint: 1,
      pos: vec2(x, z),
      prevPos: vec2(x, z),
      vel: vec2(),
      facing: team === 'blue' ? 0 : Math.PI,
      radius: stats.radius,
      speed: stats.speed,
      hp: stats.maxHp,
      maxHp: stats.maxHp,
      alive: true,
      cooldown: 0,
      targetId: 0,
      attackTicks: 999,
    };
    this.minions.push(minion);
    return minion;
  }

  /** Busca héroe, minion o estructura por id. */
  unitById(id: number): Unit | undefined {
    if (!id) return undefined;
    return (
      this.heroes.find((hero) => hero.id === id) ??
      this.minions.find((minion) => minion.id === id) ??
      this.structures.find((structure) => structure.id === id)
    );
  }

  /**
   * Torre: vulnerable cuando cayeron las de tier menor de su misma línea.
   * Nexo: vulnerable cuando alguna de sus líneas no tiene ya torres en pie.
   */
  isStructureVulnerable(structure: StructureState): boolean {
    if (!structure.alive) return false;
    const towers = this.structures.filter((o) => o.alive && o.team === structure.team && o.kind === 'tower');
    // nexo (tier 4): cuando cayeron sus dos torres (tier 3)
    if (structure.kind === 'nexus') return !towers.some((t) => t.tier === 3);
    // torres del nexo (tier 3): cuando alguna línea quedó sin torres
    if (structure.tier === 3) {
      for (const lane of this.activeLanes) {
        if (!towers.some((t) => t.lane === lane)) return true;
      }
      return false;
    }
    // torres de línea: cuando cayeron las de tier menor de su misma línea
    return !towers.some((o) => o.lane === structure.lane && o.tier < structure.tier);
  }

  /**
   * Unidades enemigas vivas que pueden recibir daño ahora mismo. Las habilidades
   * no dañan estructuras: sólo los autoataques y los minions (`withStructures`).
   */
  private enemyUnits(team: Team, withStructures = false): Unit[] {
    const out: Unit[] = [];
    for (const hero of this.heroes) if (hero.alive && hero.team !== team) out.push(hero);
    for (const minion of this.minions) if (minion.alive && minion.team !== team) out.push(minion);
    if (withStructures) {
      for (const structure of this.structures) {
        if (structure.team !== team && this.isStructureVulnerable(structure)) out.push(structure);
      }
    }
    return out;
  }

  addHero(heroId: HeroId, options: AddHeroOptions): HeroState {
    const def = getHeroDef(heroId);
    const hero: HeroState = {
      id: this.nextHeroId++,
      heroId,
      team: options.team,
      isPlayer: Boolean(options.isPlayer),
      isBot: Boolean(options.isBot),
      isDummy: Boolean(options.isDummy),
      name: options.name ?? def.displayName,
      pos: vec2(options.x, options.z),
      prevPos: vec2(options.x, options.z),
      vel: vec2(),
      spawnPos: vec2(options.x, options.z),
      facing: options.facing ?? 0,
      aim: vec2(options.x, options.z + 1),
      hp: def.maxHp,
      maxHp: def.maxHp,
      radius: def.radius,
      speed: def.speed,
      alive: true,
      respawnAtTick: 0,
      action: idleAction(),
      actionSerial: 0,
      cooldowns: emptyCooldowns(),
      charges: fullCharges(heroId),
      chargeTicks: emptyCooldowns(),
      hitstun: 0,
      invuln: 0,
      chainWindow: 0,
      attackCooldown: 0,
      comboChain: 0,
      lastEnemyHeroId: 0,
      lastEnemyHeroAt: 0,
      assistContributors: [],
      flashTicks: 0,
      deaths: 0,
      kills: 0,
      damageDealt: 0,
      lastDamagedAt: -OUT_OF_COMBAT_REGEN_TICKS,
      targetId: 0,
      gold: 0,
      recallTicks: 0,
      thornsTicks: 0,
      thornsReflect: 0,
      shieldHp: 0,
      liftTicks: 0,
      liftDuration: 0,
      grabbedBy: 0,
      level: 1,
      xp: 0,
      // sin progresión (training/tests) todo está desbloqueado en rango 1
      skillPoints: this.progression ? 1 : 0,
      ranks: this.progression ? { q: 0, w: 0, e: 0, r: 0 } : { q: 1, w: 1, e: 1, r: 1 },
      items: [],
      lane: options.lane,
    };
    this.heroes.push(hero);
    return hero;
  }

  step(inputs: Map<number, InputState>): SimEvent[] {
    this.events = [];
    if (this.match.ended) return this.events;

    this.tick++;
    if (this.match.enabled) this.match.elapsedTicks++;

    for (const hero of this.heroes) {
      hero.prevPos = cloneVec2(hero.pos);
      for (const slot of ABILITY_SLOTS) this.tickCooldown(hero, slot);
      if (hero.hitstun > 0) hero.hitstun--;
      if (hero.invuln > 0) hero.invuln--;
      if (hero.thornsTicks > 0) {
        hero.thornsTicks--;
        if (hero.thornsTicks === 0) hero.shieldHp = 0;
      }
      if (hero.flashTicks > 0) hero.flashTicks--;
      if (hero.chainWindow > 0) hero.chainWindow--;
      if (hero.attackCooldown > 0) hero.attackCooldown--;
      if (!hero.alive && this.tick >= hero.respawnAtTick) this.respawn(hero);
    }

    for (const hero of this.heroes) {
      if (!hero.alive || hero.hp >= hero.maxHp) continue;
      if (this.tick - hero.lastDamagedAt < OUT_OF_COMBAT_REGEN_TICKS) continue;
      const regen = heroStats(hero.heroId, hero.level, hero.items).hpRegen;
      hero.hp = Math.min(hero.maxHp, hero.hp + regen * TICK_DT);
    }

    for (const hero of this.heroes) {
      if (hero.isBot) {
        this.botRankUp(hero);
        this.botShop(hero);
      } else {
        const input = inputs.get(hero.id);
        if (input?.levelUp) this.rankUp(hero, input.levelUp);
        if (input?.buy) this.buyItem(hero, input.buy);
        if (input && input.sell >= 0) this.sellItem(hero, input.sell);
      }
    }

    for (const hero of this.heroes) {
      if (!hero.alive) continue;
      const raw = hero.isBot ? this.botInput(hero) : inputs.get(hero.id);
      const steered = raw && !hero.isBot ? this.steerToMoveTarget(hero, raw) : raw;
      const input = steered && !hero.isBot ? this.applyTargeting(hero, steered) : steered;
      if (input) this.updateRecall(hero, input);
      if (!hero.alive) continue;
      if (input) {
        this.handleInput(hero, input);
      } else {
        this.updateDummy(hero);
      }
      this.processAction(hero);
      this.applyMovement(hero, input);
    }

    if (this.laneEnabled) {
      this.passiveGold();
      this.healAtNexus();
      this.spawnWaves();
      this.updateStructures();
      this.updateMinions();
    }

    this.updateProjectiles();
    this.minions = this.minions.filter((minion) => minion.alive);
    this.separateHeroes();
    this.pushOutOfStructures();
    this.clampToArena();
    this.checkMatchEnd();
    return this.events;
  }

  spawnProjectile(
    ownerId: number,
    kind: ProjectileKind,
    x: number,
    z: number,
    angle: number,
    speed: number,
    damage: number,
    radius = 0.35,
    ttl = 90,
    knockback = 3,
    hitstun = 0,
  ): ProjectileState | undefined {
    const owner = this.heroes.find((hero) => hero.id === ownerId);
    if (!owner) return undefined;
    const projectile: ProjectileState = {
      id: this.nextProjectileId++,
      ownerId,
      team: owner.team,
      kind,
      pos: vec2(x, z),
      prevPos: vec2(x, z),
      vel: fromAngle(angle, speed),
      radius,
      damage,
      knockback,
      hitstun,
      ttl,
      alive: true,
      reflected: false,
      pierce: 0,
      hitIds: [],
      explodeRadius: 0,
      explodeDamage: 0,
      explodeKnockback: 0,
      explodeHitstun: 0,
      explodeOnExpire: false,
      explodeOnHit: false,
      homingId: 0,
    };
    this.projectiles.push(projectile);
    this.events.push({
      type: 'projectileSpawn',
      projectileId: projectile.id,
      x,
      z,
      kind,
    });
    return projectile;
  }

  hash(): number {
    let hash = 2166136261 >>> 0;
    const mix = (value: number): void => {
      hash ^= Math.round(value * 1000) | 0;
      hash = Math.imul(hash, 16777619);
    };
    mix(this.tick);
    for (const hero of this.heroes) {
      mix(hero.id);
      mix(hero.hp);
      mix(hero.pos.x);
      mix(hero.pos.z);
      mix(hero.facing);
      mix(hero.hitstun);
      mix(hero.alive ? 1 : 0);
      mix(hero.kills);
      mix(hero.gold);
      mix(hero.recallTicks);
      mix(hero.thornsTicks);
      mix(hero.shieldHp);
      mix(hero.liftTicks);
      mix(hero.level);
      mix(hero.xp);
      mix(hero.action.ticks);
      mix(hero.action.kind.charCodeAt(0));
    }
    for (const structure of this.structures) {
      mix(structure.id);
      mix(structure.hp);
      mix(structure.targetId);
    }
    for (const minion of this.minions) {
      mix(minion.id);
      mix(minion.hp);
      mix(minion.pos.x);
      mix(minion.pos.z);
    }
    for (const projectile of this.projectiles) {
      mix(projectile.id);
      mix(projectile.pos.x);
      mix(projectile.pos.z);
    }
    mix(this.match.score.blue);
    mix(this.match.score.red);
    return hash >>> 0;
  }

  /** Con `moveTo`, la dirección sale de la posición real del héroe y se para al llegar. */
  private steerToMoveTarget(hero: HeroState, input: InputState): InputState {
    const target = input.moveTo;
    if (!target) return input;
    const dx = target.x - hero.pos.x;
    const dz = target.z - hero.pos.z;
    const dist = Math.hypot(dx, dz);
    // un paso por tick ronda 0.2 u: por debajo de esto ya ha llegado
    if (dist <= MOVE_ARRIVE_RADIUS) return { ...input, moveX: 0, moveZ: 0 };
    return { ...input, moveX: dx / dist, moveZ: dz / dist };
  }

  private handleInput(hero: HeroState, input: InputState): void {
    if (input.aimX !== 0 || input.aimZ !== 0) {
      hero.aim = vec2(input.aimX, input.aimZ);
    }

    for (const slot of ABILITY_SLOTS) {
      if (input.abilities[slot] && this.detonateRecast(hero, slot)) return;
    }

    // Las pulsaciones de habilidad se guardan unos ticks: si el héroe está en
    // medio de otra habilidad o en hitstun, se lanzan en cuanto pueda.
    for (const slot of ABILITY_SLOTS) {
      if (input.abilities[slot] && hero.cooldowns[slot] <= 0 && hero.ranks[slot] > 0) {
        hero.bufferedSlot = slot;
        hero.bufferedUntil = this.tick + ABILITY_BUFFER_TICKS;
        break;
      }
    }

    if (hero.hitstun > 0) return;

    const action = hero.action;
    // quieto no sigue al ratón: mira a su objetivo fijado (si lo hay) o se queda
    // como estaba; al andar mira hacia donde va (`moveHero`) y al lanzar o
    // atacar, hacia donde sale el golpe
    if (action.kind === 'idle') {
      const target = this.validTarget(hero, hero.targetId);
      const delta = target ? sub(target.pos, hero.pos) : undefined;
      if (delta && lengthSq(delta) > 1e-6) hero.facing = angleOf(delta);
    }

    const buffered = hero.bufferedSlot;
    if (buffered) {
      if (this.tick > (hero.bufferedUntil ?? 0) || hero.cooldowns[buffered] > 0) {
        hero.bufferedSlot = undefined;
      } else if (this.canStartAbility(hero, true)) {
        hero.bufferedSlot = undefined;
        // sin objetivo válido (ejecución) no se lanza y el héroe sigue con lo suyo
        if (this.startAbility(hero, buffered)) return;
      }
    }

    // La velocidad de ataque marca el tiempo ENTRE golpes (attackCooldown); la
    // animación del golpe dura lo de siempre. El combo sigue si el siguiente
    // golpe sale dentro de su ventana.
    if (input.attack && hero.attackCooldown <= 0) {
      const ready = action.kind === 'idle' || (action.kind === 'attack' && this.attackHitDone(hero, action));
      if (ready) {
        const chain = hero.chainWindow > 0 ? (hero.comboChain + 1) % 3 : 0;
        this.startAttack(hero, chain);
      }
    }
  }

  /** ¿Puede comprar? En su base (cerca del nexo propio) o estando muerto. */
  canShop(hero: HeroState): boolean {
    if (!hero.alive) return true;
    const nexus = this.structures.find((s) => s.kind === 'nexus' && s.team === hero.team);
    // sin línea (training/tests) la tienda está junto al punto de aparición
    const shopPos = nexus ? nexus.pos : hero.spawnPos;
    return distance(hero.pos, shopPos) <= SHOP.radius;
  }

  buyItem(hero: HeroState, itemId: string): boolean {
    const item = getItem(itemId);
    if (!item || !this.canShop(hero)) return false;
    if (hero.items.length >= SHOP.slots || hero.gold < item.cost) return false;
    hero.gold -= item.cost;
    hero.items.push(itemId);
    this.refreshMaxHp(hero);
    this.events.push({ type: 'shop', heroId: hero.id, action: 'buy', itemId });
    return true;
  }

  sellItem(hero: HeroState, slot: number): boolean {
    const itemId = hero.items[slot];
    if (!itemId || !this.canShop(hero)) return false;
    hero.items.splice(slot, 1);
    hero.gold += sellPrice(itemId);
    this.refreshMaxHp(hero);
    this.events.push({ type: 'shop', heroId: hero.id, action: 'sell', itemId });
    return true;
  }

  /** Recalcula la vida máxima (nivel + objetos) conservando la vida perdida. */
  private refreshMaxHp(hero: HeroState): void {
    const maxHp = heroStats(hero.heroId, hero.level, hero.items).maxHp;
    if (hero.alive) hero.hp = Math.max(1, Math.min(maxHp, hero.hp + (maxHp - hero.maxHp)));
    hero.maxHp = maxHp;
  }

  /** Los bots compran en la base el siguiente objeto de su build que puedan pagar. */
  private botShop(hero: HeroState): void {
    if (!this.progression || !this.canShop(hero)) return;
    const build = BOT_BUILDS[botRole(hero.heroId)] ?? BOT_BUILDS.fighter;
    for (const itemId of build) {
      if (hero.items.length >= SHOP.slots) return;
      if (hero.items.includes(itemId)) continue;
      if (hero.gold < (ITEMS[itemId]?.cost ?? Infinity)) return; // ahorra para el siguiente
      this.buyItem(hero, itemId);
    }
  }

  /** Gasta un punto en subir el rango de una habilidad, si las reglas lo permiten. */
  rankUp(hero: HeroState, slot: AbilitySlot): boolean {
    if (!canRankUp(hero, slot)) return false;
    hero.ranks[slot]++;
    hero.skillPoints--;
    return true;
  }

  private botRankUp(hero: HeroState): void {
    while (hero.skillPoints > 0) {
      const slot = BOT_RANK_PRIORITY.find((s) => canRankUp(hero, s));
      if (!slot) return;
      this.rankUp(hero, slot);
    }
  }

  /** Suma experiencia y sube de nivel (vida máxima y punto de habilidad). */
  gainXp(hero: HeroState, amount: number): void {
    if (hero.level >= MAX_LEVEL || amount <= 0) return;
    hero.xp += amount;
    while (hero.level < MAX_LEVEL && hero.xp >= xpToNext(hero.level)) {
      hero.xp -= xpToNext(hero.level);
      hero.level++;
      hero.skillPoints++;
      const maxHp = heroStats(hero.heroId, hero.level, hero.items).maxHp;
      if (hero.alive) hero.hp += maxHp - hero.maxHp;
      hero.maxHp = maxHp;
      this.events.push({ type: 'levelUp', heroId: hero.id, level: hero.level, x: hero.pos.x, z: hero.pos.z });
    }
    if (hero.level >= MAX_LEVEL) hero.xp = 0;
  }

  /**
   * Minion que muere sin golpe final de un héroe (torre o minion): cada héroe
   * enemigo cercano recibe un tercio del oro en vez de nada.
   */
  private awardMissedMinionGold(minion: MinionState): void {
    const amount = Math.round(GOLD.minion * GOLD.minionMissedShare);
    for (const hero of this.heroes) {
      if (!hero.alive || hero.isDummy || hero.team === minion.team || distance(hero.pos, minion.pos) > XP.range) continue;
      this.giveGold(hero, amount, minion.pos);
    }
  }

  /** Experiencia de un minion enemigo, repartida entre los héroes cercanos. */
  private awardMinionXp(minion: MinionState): void {
    const near = this.heroes.filter(
      (h) => h.alive && h.team !== minion.team && distance(h.pos, minion.pos) <= XP.range,
    );
    for (const hero of near) this.gainXp(hero, XP.minion / near.length);
  }

  /**
   * Regreso a base: B empieza una canalización quieta de RECALL_TICKS; moverse,
   * atacar, lanzar, fijar un objetivo, ser aturdido o recibir daño la cancela.
   */
  private updateRecall(hero: HeroState, input: InputState): void {
    const acting =
      input.moveX !== 0 ||
      input.moveZ !== 0 ||
      input.attack ||
      input.targetId > 0 ||
      input.abilities.q || input.abilities.w || input.abilities.e || input.abilities.r;
    if (hero.recallTicks > 0) {
      if (acting || hero.hitstun > 0 || hero.action.kind !== 'idle') {
        this.cancelRecall(hero);
        return;
      }
      hero.recallTicks++;
      if (hero.recallTicks >= RECALL_TICKS) {
        hero.recallTicks = 0;
        hero.pos = cloneVec2(hero.spawnPos);
        hero.prevPos = cloneVec2(hero.spawnPos);
        hero.vel = vec2();
        hero.targetId = 0;
        this.events.push({ type: 'recall', heroId: hero.id, phase: 'done', x: hero.pos.x, z: hero.pos.z });
      }
      return;
    }
    if (input.recall && !acting && hero.hitstun <= 0 && hero.action.kind === 'idle') {
      hero.recallTicks = 1;
      this.events.push({ type: 'recall', heroId: hero.id, phase: 'start', x: hero.pos.x, z: hero.pos.z });
    }
  }

  private cancelRecall(hero: HeroState): void {
    if (hero.recallTicks <= 0) return;
    hero.recallTicks = 0;
    this.events.push({ type: 'recall', heroId: hero.id, phase: 'cancel', x: hero.pos.x, z: hero.pos.z });
  }

  /** Detona un proyectil propio reactivable de esa habilidad, si existe. */
  private detonateRecast(hero: HeroState, slot: AbilitySlot): boolean {
    const live = this.projectiles.find((p) => p.alive && p.ownerId === hero.id && p.recastSlot === slot);
    if (!live) return false;
    if (live.explodeRadius > 0) this.explodeProjectile(live);
    live.alive = false;
    return true;
  }

  /** Ventana de impacto de un autoataque, escalada si la animación se comprimió. */
  private attackWindow(hero: HeroState, action: ActionState): { start: number; end: number } {
    const strike = getHeroDef(hero.heroId).attacks[Math.min(action.chain, getHeroDef(hero.heroId).attacks.length - 1)];
    const timingScale = action.duration / strike.duration;
    const start = Math.floor(strike.activeStart * timingScale);
    return { start, end: Math.max(start, Math.ceil(strike.activeEnd * timingScale)) };
  }

  /** ¿Ya salió el golpe/disparo de este autoataque? */
  private attackHitDone(hero: HeroState, action: ActionState): boolean {
    return action.hitIds.length > 0 || action.ticks > this.attackWindow(hero, action).end;
  }

  /**
   * ¿Pulsar `slot` ahora lanzaría la habilidad en este mismo tick? (mismas reglas
   * que `handleInput`/`startAbility`). El cliente online lo usa para empezar la
   * animación del propio héroe sin esperar al servidor.
   */
  wouldStartAbility(hero: HeroState, slot: AbilitySlot): boolean {
    if (!hero.alive || hero.hitstun > 0 || hero.grabbedBy !== 0) return false;
    if (hero.cooldowns[slot] > 0 || hero.ranks[slot] <= 0) return false;
    const ability = getHeroDef(hero.heroId).abilities[slot];
    // con una bomba propia en vuelo, la tecla la detona en vez de lanzar (el
    // snapshot no trae `recastSlot`: también se reconoce por el tipo de proyectil)
    const recast = ability.kind === 'projectile' && ability.projectile.detonateOnRecast ? ability.projectile.kind : undefined;
    if (this.projectiles.some((p) => p.alive && p.ownerId === hero.id && (p.recastSlot === slot || (recast !== undefined && p.kind === recast)))) {
      return false;
    }
    if (!this.canStartAbility(hero, true)) return false;
    if (ability.kind === 'execute' && !this.findExecuteTarget(hero, ability.execute)) return false;
    return true;
  }

  /** `forAbility`: las habilidades cancelan el autoataque en cualquier momento. */
  private canStartAbility(hero: HeroState, forAbility = false): boolean {
    const action = hero.action;
    if (action.kind === 'idle') return true;
    if (action.kind === 'attack' && (forAbility || action.ticks >= action.duration * 0.55)) return true;
    if (action.kind === 'cast' && action.ticks >= action.duration * 0.85) return true;
    return false;
  }

  private startAttack(hero: HeroState, chain: number): void {
    const def = getHeroDef(hero.heroId);
    const strike = def.attacks[Math.min(chain, def.attacks.length - 1)];
    const speedScale = heroStats(hero.heroId, hero.level, hero.items).attackSpeed;
    const interval = Math.max(1, Math.round(strike.duration / speedScale));
    hero.attackCooldown = interval;
    hero.comboChain = chain;
    hero.chainWindow = interval + CHAIN_WINDOW_TICKS;
    hero.action = {
      kind: 'attack',
      ticks: 0,
      // la animación sólo se acelera si el intervalo es más corto que ella
      duration: Math.min(strike.duration, interval),
      chain,
      hitIds: [],
    };
    hero.actionSerial++;
    hero.facing = this.attackFacing(hero);
  }

  /**
   * Autoataque por objetivo: si el héroe tiene un enemigo fijado, lo persigue
   * hasta entrar en rango y lo ataca en bucle. Devuelve el input efectivo.
   */
  private applyTargeting(hero: HeroState, input: InputState): InputState {
    const target = this.validTarget(hero, input.targetId);
    hero.targetId = target ? target.id : 0;
    if (!target) return input;

    const basic = getHeroDef(hero.heroId).attacks[0];
    const range = basic.range * heroStats(hero.heroId, hero.level, hero.items).attackRange;
    const reach = range + target.radius - 0.15;
    const dx = target.pos.x - hero.pos.x;
    const dz = target.pos.z - hero.pos.z;
    const dist = Math.hypot(dx, dz);
    const out: InputState = { ...input, abilities: { ...input.abilities } };
    if (dist > reach) {
      out.moveX = dist > 1e-5 ? dx / dist : 0;
      out.moveZ = dist > 1e-5 ? dz / dist : 0;
    } else {
      out.moveX = 0;
      out.moveZ = 0;
      out.attack = true;
    }
    return out;
  }

  private validTarget(hero: HeroState, targetId: number): Unit | undefined {
    const target = this.unitById(targetId);
    if (!target || !target.alive || target.team === hero.team) return undefined;
    if (isStructureUnit(target) && !this.isStructureVulnerable(target)) return undefined;
    return target;
  }

  private attackFacing(hero: HeroState): number {
    const target = this.validTarget(hero, hero.targetId);
    if (target) {
      const delta = sub(target.pos, hero.pos);
      if (lengthSq(delta) > 1e-6) return angleOf(delta);
    }
    return this.facingFromAim(hero);
  }

  /** Cooldown completo de la habilidad con su rango y la aceleración actual. */
  private fullCooldown(hero: HeroState, slot: AbilitySlot): number {
    const ability = getHeroDef(hero.heroId).abilities[slot];
    const haste = abilityHasteMultiplier(heroStats(hero.heroId, hero.level, hero.items).abilityHaste);
    return Math.round(abilityCooldown(ability) * abilityCooldownScale(slot, hero.ranks[slot]) * haste);
  }

  /** Ticks para recuperar una carga: el cooldown completo repartido entre las cargas. */
  private rechargeTicks(hero: HeroState, slot: AbilitySlot): number {
    const max = abilityCharges(getHeroDef(hero.heroId).abilities[slot]);
    return Math.max(1, Math.round(this.fullCooldown(hero, slot) / max));
  }

  /**
   * Gasta un uso: sin cargas, cooldown completo. Con cargas, consume una; si
   * quedan, empieza a recargar (cooldown / cargas cada una) y se puede volver a
   * lanzar ya; si era la última, cooldown completo, que devuelve todas.
   */
  private spendAbility(hero: HeroState, slot: AbilitySlot): void {
    const max = abilityCharges(getHeroDef(hero.heroId).abilities[slot]);
    if (max <= 1) {
      hero.cooldowns[slot] = this.fullCooldown(hero, slot);
      return;
    }
    hero.charges[slot] = Math.max(0, Math.min(max, hero.charges[slot]) - 1);
    if (hero.charges[slot] === 0) {
      hero.cooldowns[slot] = this.fullCooldown(hero, slot);
      hero.chargeTicks[slot] = 0;
    } else if (hero.chargeTicks[slot] <= 0) {
      hero.chargeTicks[slot] = this.rechargeTicks(hero, slot);
    }
  }

  private tickCooldown(hero: HeroState, slot: AbilitySlot): void {
    const max = abilityCharges(getHeroDef(hero.heroId).abilities[slot]);
    if (hero.cooldowns[slot] > 0) {
      hero.cooldowns[slot]--;
      if (hero.cooldowns[slot] === 0 && max > 1) hero.charges[slot] = max;
    }
    if (hero.chargeTicks[slot] > 0) {
      hero.chargeTicks[slot]--;
      if (hero.chargeTicks[slot] === 0) {
        hero.charges[slot] = Math.min(max, hero.charges[slot] + 1);
        if (hero.charges[slot] < max) hero.chargeTicks[slot] = this.rechargeTicks(hero, slot);
      }
    }
  }

  private startAbility(hero: HeroState, slot: AbilitySlot): boolean {
    const def = getHeroDef(hero.heroId);
    const ability = def.abilities[slot];
    const executeTarget = ability.kind === 'execute' ? this.findExecuteTarget(hero, ability.execute) : undefined;
    if (ability.kind === 'execute' && !executeTarget) return false;
    hero.facing = executeTarget ? angleOf(sub(executeTarget.pos, hero.pos)) : this.facingFromAim(hero);
    hero.action = {
      kind: 'cast',
      slot,
      ticks: 0,
      duration: abilityDuration(ability),
      chain: 0,
      hitIds: [],
    };
    hero.actionSerial++;
    this.spendAbility(hero, slot);

    if (ability.kind === 'strike') {
      const strike = ability.strike;
      if (strike.invulnTicks > 0) {
        hero.invuln = Math.max(hero.invuln, strike.invulnTicks);
      }
      if (strike.sweep) hero.action.origin = cloneVec2(hero.pos);
    } else if (ability.kind === 'projectile') {
      if (ability.projectile.fireAtTick <= 0) this.fireAbilityProjectiles(hero, ability.projectile, slot);
    } else if (ability.kind === 'blink') {
      this.doBlink(hero, ability.blink, abilityPower(hero.heroId, hero.level, slot, hero.ranks[slot], hero.items));
    } else if (ability.kind === 'shield') {
      // escudo de espinas: el buff empieza ya; el lanzamiento es sólo la pose
      hero.thornsTicks = ability.shield.durationTicks;
      hero.thornsReflect = ability.shield.reflect;
      hero.shieldHp = ability.shield.absorb;
    } else if (ability.kind === 'execute' && executeTarget) {
      hero.action.targetId = executeTarget.id;
      this.cancelRecall(hero);
      this.events.push({
        type: 'execute',
        heroId: hero.id,
        targetId: executeTarget.id,
        phase: 'mark',
        x: executeTarget.pos.x,
        z: executeTarget.pos.z,
        delay: ability.execute.strikeTick,
        amount: 0,
      });
    }
    return true;
  }

  /**
   * Objetivo de la ejecución: el héroe enemigo fijado si está a su alcance; si
   * no, el más cercano al cursor (dentro de `pickRadius`) y, si tampoco, el más
   * cercano al héroe dentro del alcance.
   */
  private findExecuteTarget(hero: HeroState, spec: ExecuteSpec): HeroState | undefined {
    const candidates = this.heroes.filter(
      (other) => other.alive && other.team !== hero.team && distance(other.pos, hero.pos) <= spec.range + other.radius,
    );
    if (candidates.length === 0) return undefined;
    const locked = candidates.find((other) => other.id === hero.targetId);
    if (locked) return locked;
    let best: HeroState | undefined;
    let bestDist = Infinity;
    for (const other of candidates) {
      const d = distance(other.pos, hero.aim);
      if (d <= spec.pickRadius + other.radius && d < bestDist) {
        best = other;
        bestDist = d;
      }
    }
    if (best) return best;
    for (const other of candidates) {
      const d = distance(other.pos, hero.pos);
      if (d < bestDist) {
        best = other;
        bestDist = d;
      }
    }
    return best;
  }

  /** Daño que haría la ejecución ahora mismo (antes de escudos; ignora la armadura). */
  executeDamage(hero: HeroState, target: HeroState, spec: ExecuteSpec, slot: AbilitySlot = 'r'): number {
    const rank = Math.max(1, hero.ranks[slot]);
    const ratio = spec.missingHp[Math.min(spec.missingHp.length - 1, rank - 1)] ?? 0;
    const power = abilityPower(hero.heroId, hero.level, slot, rank, hero.items);
    return spec.damage * power + ratio * Math.max(0, target.maxHp - target.hp);
  }

  /** La espada cae: daño puro sobre el objetivo si sigue vivo y no se ha escapado. */
  private processExecute(hero: HeroState, spec: ExecuteSpec, action: ActionState, slot: AbilitySlot): void {
    const target = this.heroes.find((other) => other.id === action.targetId);
    if (target && target.alive) hero.facing = angleOf(sub(target.pos, hero.pos));
    if (action.ticks !== spec.strikeTick || !target || !target.alive) return;
    if (distance(target.pos, hero.pos) > spec.range * spec.leash + target.radius) return;
    const amount = this.executeDamage(hero, target, spec, slot);
    const hpBefore = target.hp;
    this.dealDamage(hero, target, amount, vec2(), 0, 0, true, hero.id, false, true);
    this.events.push({
      type: 'execute',
      heroId: hero.id,
      targetId: target.id,
      phase: 'strike',
      x: target.pos.x,
      z: target.pos.z,
      delay: 0,
      amount: Math.round(hpBefore - target.hp),
    });
  }

  private fireAbilityProjectiles(hero: HeroState, spec: ProjectileSpec, slot: AbilitySlot): void {
    const base = hero.facing;
    const count = Math.max(1, spec.count);
    const totalSpread = spec.spread * Math.max(0, count - 1);
    for (let i = 0; i < count; i++) {
      const angle = count === 1 ? base : base - totalSpread / 2 + (totalSpread * i) / (count - 1);
      const projectile: ProjectileState = {
        id: this.nextProjectileId++,
        ownerId: hero.id,
        team: hero.team,
        kind: spec.kind,
        pos: cloneVec2(hero.pos),
        prevPos: cloneVec2(hero.pos),
        vel: fromAngle(angle, spec.speed),
        radius: spec.radius,
        damage: spec.damage * abilityPower(hero.heroId, hero.level, slot, hero.ranks[slot], hero.items),
        knockback: spec.knockback,
        hitstun: spec.stun ? spec.hitstun : 0,
        ttl: spec.ttl,
        alive: true,
        reflected: false,
        pierce: spec.pierce,
        hitIds: [],
        explodeRadius: spec.explodeRadius,
        explodeDamage: spec.explodeDamage * abilityPower(hero.heroId, hero.level, slot, hero.ranks[slot], hero.items),
        explodeKnockback: spec.explodeKnockback,
        explodeHitstun: spec.stun ? spec.explodeHitstun : 0,
        explodeOnExpire: spec.explodeOnExpire,
        explodeOnHit: spec.explodeOnHit,
        homingId: 0,
        recastSlot: spec.detonateOnRecast ? slot : undefined,
      };
      this.projectiles.push(projectile);
      this.events.push({
        type: 'projectileSpawn',
        projectileId: projectile.id,
        x: projectile.pos.x,
        z: projectile.pos.z,
        kind: spec.kind,
      });
    }
  }

  private doBlink(hero: HeroState, spec: BlinkSpec, scale = 1): void {
    const dir = fromAngle(hero.facing);
    let nx = hero.pos.x + dir.x * spec.distance;
    let nz = hero.pos.z + dir.z * spec.distance;
    const maxRadius = ARENA_RADIUS - hero.radius;
    const dist = Math.hypot(nx, nz);
    if (dist > maxRadius) {
      nx = (nx / dist) * maxRadius;
      nz = (nz / dist) * maxRadius;
    }
    hero.pos = vec2(nx, nz);
    hero.prevPos = vec2(nx, nz);
    if (spec.invulnTicks > 0) {
      hero.invuln = Math.max(hero.invuln, spec.invulnTicks);
    }
    if (spec.damage > 0 && spec.radius > 0) {
      for (const target of this.enemyUnits(hero.team)) {
        if (distance(target.pos, hero.pos) > spec.radius + target.radius) continue;
        const dirTo = normalize(sub(target.pos, hero.pos));
        this.damageUnit(hero, hero.id, target, spec.damage * scale, dirTo, spec.knockback, 0, true);
      }
    }
  }

  private processAction(hero: HeroState): void {
    const action = hero.action;
    if (action.kind === 'idle' || action.kind === 'dead') return;

    action.ticks++;

    if (action.kind === 'hit') {
      if (action.ticks >= action.duration || hero.hitstun <= 0) {
        this.finishAction(hero);
      }
      return;
    }

    const strike = this.strikeFor(hero, action);
    if (!strike) {
      if (action.kind === 'cast' && action.slot) {
        const ability = getHeroDef(hero.heroId).abilities[action.slot];
        if (ability.kind === 'grab') {
          this.processGrab(hero, ability.grab, action);
          return;
        }
        if (ability.kind === 'execute') this.processExecute(hero, ability.execute, action, action.slot);
        if (ability.kind === 'projectile' && ability.projectile.fireAtTick > 0 && action.ticks === ability.projectile.fireAtTick) {
          this.fireAbilityProjectiles(hero, ability.projectile, action.slot);
        }
      }
      if (action.ticks >= action.duration) this.finishAction(hero);
      return;
    }

    // La velocidad de ataque escala la duración del autoataque (ya aplicada en
    // `startAttack`); re-escalamos la ventana de impacto en la misma proporción.
    // floor/ceil garantizan que una ventana de un solo tick siga conteniendo un tick.
    const timingScale = action.duration / strike.duration;
    const activeStart = Math.floor(strike.activeStart * timingScale);
    const activeEnd = Math.max(activeStart, Math.ceil(strike.activeEnd * timingScale));

    if (strike.invulnTicks > 0 && action.ticks <= strike.invulnTicks) {
      hero.invuln = Math.max(hero.invuln, 1);
    }

    // gira sobre sí mismo hacia el cursor (aliento de Bowser), con velocidad limitada
    if (strike.turnToAim > 0 && action.kind === 'cast') {
      const delta = sub(hero.aim, hero.pos);
      if (lengthSq(delta) > 0.01) {
        const diff = Math.atan2(Math.sin(angleOf(delta) - hero.facing), Math.cos(angleOf(delta) - hero.facing));
        const maxTurn = strike.turnToAim * TICK_DT;
        hero.facing += Math.max(-maxTurn, Math.min(maxTurn, diff));
      }
    }

    if (strike.slamOnContact && strike.dashTicks > 0 && action.ticks <= strike.dashTicks) {
      const contact = this.enemyUnits(hero.team).some(
        (unit) => distance(unit.pos, hero.pos) <= hero.radius + unit.radius + 0.4,
      );
      if (contact) action.ticks = Math.max(action.ticks, strike.activeStart);
    }

    if (strike.dashTicks > 0 && action.ticks <= strike.dashTicks) {
      hero.pos = add(hero.pos, scale(fromAngle(hero.facing), strike.dashSpeed * TICK_DT));
    }

    if (action.ticks >= activeStart && action.ticks <= activeEnd) {
      const isRehitBoundary =
        strike.rehitEvery > 0 &&
        action.ticks > activeStart &&
        (action.ticks - activeStart) % strike.rehitEvery === 0;
      if (isRehitBoundary) action.hitIds = [];

      const rangedAttack = action.kind === 'attack' && getHeroDef(hero.heroId).ranged;
      if ((action.ticks === activeStart || isRehitBoundary) && !rangedAttack) {
        this.events.push({
          type: 'attack',
          sourceId: hero.id,
          slot: action.kind === 'attack' ? 'attack' : (action.slot ?? 'q'),
          x: hero.pos.x,
          z: hero.pos.z,
          facing: hero.facing,
          range: strike.range,
          arc: strike.arc,
          heavy: strike.heavy,
        });
      }

      this.performStrike(hero, strike, action);
    }

    if (action.ticks >= action.duration) {
      this.finishAction(hero);
    }
  }

  /**
   * Agarre aéreo: embestida; al tocar a un héroe enemigo lo agarra, ambos
   * suben por los aires `liftTicks` y Bowser lo estrella contra el suelo.
   * Mientras lo sostiene, Bowser es imparable y el agarrado no puede actuar.
   */
  private processGrab(hero: HeroState, spec: GrabSpec, action: ActionState): void {
    if (action.grabbedId === undefined) {
      if (action.ticks <= spec.dashTicks) {
        hero.pos = add(hero.pos, scale(fromAngle(hero.facing), spec.dashSpeed * TICK_DT));
        const victim = this.heroes.find(
          (other) =>
            other.alive &&
            other.team !== hero.team &&
            other.invuln <= 0 &&
            other.grabbedBy === 0 &&
            withinArc(hero.pos, hero.facing, other.pos, hero.radius + other.radius + spec.reach, 200 * (Math.PI / 180)),
        );
        if (victim) this.seize(hero, victim, spec, action);
        return;
      }
      // falló: sólo la recuperación
      if (action.ticks >= spec.dashTicks + spec.recoveryTicks) this.finishAction(hero);
      return;
    }

    const victim = this.heroes.find((other) => other.id === action.grabbedId);
    const elapsed = action.ticks - (action.grabTick ?? action.ticks);
    if (!victim || !victim.alive || victim.grabbedBy !== hero.id) {
      // lo perdió (murió por otro golpe): se acaba el vuelo
      this.releaseGrab(hero, victim);
      action.duration = Math.min(action.duration, action.ticks + spec.recoveryTicks);
    } else if (elapsed < spec.liftTicks) {
      hero.liftTicks = elapsed + 1;
      hero.liftDuration = spec.liftTicks;
      victim.liftTicks = elapsed + 1;
      victim.liftDuration = spec.liftTicks;
      this.holdVictim(hero, victim);
    } else {
      // estrellón
      this.holdVictim(hero, victim);
      this.releaseGrab(hero, victim);
      action.duration = action.ticks + spec.recoveryTicks;
      const power = abilityPower(hero.heroId, hero.level, 'r', hero.ranks.r, hero.items);
      victim.hitstun = 0; // el aturdimiento del estrellón sustituye al del agarre
      this.damageUnit(hero, hero.id, victim, spec.damage * power, fromAngle(hero.facing), spec.knockback, spec.stun, true);
      for (const unit of this.enemyUnits(hero.team)) {
        if (unit.id === victim.id || distance(unit.pos, victim.pos) > spec.splashRadius + unit.radius) continue;
        this.damageUnit(hero, hero.id, unit, spec.splashDamage * power, normalize(sub(unit.pos, victim.pos)), spec.knockback, 0, true);
      }
      this.events.push({
        type: 'attack',
        sourceId: hero.id,
        slot: 'r',
        x: victim.pos.x,
        z: victim.pos.z,
        facing: hero.facing,
        range: spec.splashRadius,
        arc: Math.PI * 2,
        heavy: true,
      });
    }
    if (action.ticks >= action.duration) this.finishAction(hero);
  }

  private seize(hero: HeroState, victim: HeroState, spec: GrabSpec, action: ActionState): void {
    action.grabbedId = victim.id;
    action.grabTick = action.ticks;
    action.duration = action.ticks + spec.liftTicks + spec.recoveryTicks + 1;
    victim.grabbedBy = hero.id;
    victim.vel = vec2();
    victim.targetId = 0;
    victim.bufferedSlot = undefined;
    this.cancelRecall(victim);
    // agarre: no lo reduce la tenacidad ni lo corta nada
    const hold = spec.liftTicks + 2;
    victim.hitstun = Math.max(victim.hitstun, hold);
    victim.action = { kind: 'hit', ticks: 0, duration: hold, chain: 0, hitIds: [] };
    victim.actionSerial++;
    this.holdVictim(hero, victim);
  }

  /** El agarrado va delante de Bowser, pegado a él y mirándolo. */
  private holdVictim(hero: HeroState, victim: HeroState): void {
    victim.pos = add(hero.pos, scale(fromAngle(hero.facing), hero.radius * 0.8 + victim.radius * 0.8));
    victim.vel = vec2();
    victim.facing = hero.facing + Math.PI;
  }

  private releaseGrab(hero: HeroState, victim: HeroState | undefined): void {
    hero.liftTicks = 0;
    hero.liftDuration = 0;
    if (victim && victim.grabbedBy === hero.id) {
      victim.grabbedBy = 0;
      victim.liftTicks = 0;
      victim.liftDuration = 0;
    }
  }

  /** ¿Está sosteniendo a alguien con un agarre? (entonces es imparable) */
  private isGrabbing(hero: HeroState): boolean {
    return hero.action.kind === 'cast' && hero.action.grabbedId !== undefined && hero.liftTicks > 0;
  }

  private strikeFor(hero: HeroState, action: ActionState): StrikeDef | null {
    const def = getHeroDef(hero.heroId);
    switch (action.kind) {
      case 'attack':
        return def.attacks[Math.min(action.chain, def.attacks.length - 1)];
      case 'cast': {
        const ability = def.abilities[action.slot ?? 'q'];
        return ability.kind === 'strike' ? ability.strike : null;
      }
      default:
        return null;
    }
  }

  private performStrike(hero: HeroState, strike: StrikeDef, action: ActionState): void {
    if (action.kind === 'attack') {
      this.performBasicAttack(hero, strike, action);
      return;
    }
    const scale = action.slot ? abilityPower(hero.heroId, hero.level, action.slot, hero.ranks[action.slot], hero.items) : 1;
    for (const target of this.enemyUnits(hero.team)) {
      if (action.hitIds.includes(target.id)) continue;
      const inside = strike.sweep && action.origin
        // rectángulo (cápsula) del ancho del héroe entre el inicio del dash y su posición
        ? segmentDistance(target.pos, action.origin, hero.pos) <= hero.radius + target.radius
        : withinArc(hero.pos, hero.facing, target.pos, strike.range + target.radius, strike.arc);
      if (!inside) continue;
      action.hitIds.push(target.id);
      this.applyHit(hero, target, strike, scale);
    }
  }

  /**
   * Autoataque: golpea a UN solo objetivo. Si hay uno fijado y está en rango, a
   * ése; si no (ataque direccional de los bots), al enemigo más cercano del arco.
   */
  private performBasicAttack(hero: HeroState, strike: StrikeDef, action: ActionState): void {
    if (action.hitIds.length > 0) return;
    const range = strike.range * heroStats(hero.heroId, hero.level, hero.items).attackRange;
    let target: Unit | undefined;
    const locked = this.validTarget(hero, hero.targetId);
    if (locked && distance(hero.pos, locked.pos) <= range + locked.radius + 0.3) {
      target = locked;
    } else {
      let best = Infinity;
      for (const unit of this.enemyUnits(hero.team, true)) {
        if (!withinArc(hero.pos, hero.facing, unit.pos, range + unit.radius, strike.arc)) continue;
        const d = distanceSq(hero.pos, unit.pos);
        if (d < best) {
          best = d;
          target = unit;
        }
      }
    }
    if (!target) return;
    action.hitIds.push(target.id);
    const ranged = getHeroDef(hero.heroId).ranged;
    if (ranged) {
      this.fireRangedAttack(hero, target, strike, ranged.projectile, ranged.speed);
      return;
    }
    this.applyHit(hero, target, strike, attackDamageRatio(hero.heroId, hero.level, hero.items));
  }

  /** Autoataque de tirador: proyectil teledirigido que sólo puede impactar a su objetivo. */
  private fireRangedAttack(hero: HeroState, target: Unit, strike: StrikeDef, kind: ProjectileKind, speed: number): void {
    const angle = angleTo(hero.pos, target.pos);
    hero.facing = angle;
    const start = add(hero.pos, fromAngle(angle, hero.radius + 0.2));
    const projectile: ProjectileState = {
      id: this.nextProjectileId++,
      ownerId: hero.id,
      team: hero.team,
      kind,
      pos: cloneVec2(start),
      prevPos: cloneVec2(start),
      vel: fromAngle(angle, speed),
      radius: 0.25,
      damage: strike.damage * attackDamageRatio(hero.heroId, hero.level, hero.items),
      knockback: strike.knockback,
      hitstun: 0,
      ttl: Math.ceil(((strike.range * heroStats(hero.heroId, hero.level, hero.items).attackRange + 6) / speed) * TICK_RATE),
      alive: true,
      reflected: false,
      pierce: 0,
      hitIds: [],
      explodeRadius: 0,
      explodeDamage: 0,
      explodeKnockback: 0,
      explodeHitstun: 0,
      explodeOnExpire: false,
      explodeOnHit: false,
      homingId: target.id,
    };
    this.projectiles.push(projectile);
    this.events.push({ type: 'projectileSpawn', projectileId: projectile.id, x: start.x, z: start.z, kind });
  }

  private applyHit(attacker: HeroState, target: Unit, strike: StrikeDef, scale = 1): boolean {
    const dir = this.knockbackDirection(attacker.pos, target.pos, attacker.facing);
    const stun = strike.stun ? strike.hitstun : 0;
    return this.damageUnit(attacker, attacker.id, target, strike.damage * scale, dir, strike.knockback, stun, strike.heavy);
  }

  /** Daño a cualquier unidad. `attacker` es el héroe responsable (si lo hay). */
  private damageUnit(
    attacker: HeroState | undefined,
    sourceId: number,
    target: Unit,
    amount: number,
    kbDir: Vec2,
    knockback: number,
    hitstun: number,
    heavy: boolean,
    /** daño devuelto por espinas: ignora armadura y no vuelve a reflejarse */
    reflected = false,
  ): boolean {
    if (isHeroUnit(target)) {
      return this.dealDamage(attacker, target, amount, kbDir, knockback, hitstun, heavy, sourceId, reflected);
    }
    if (isStructureUnit(target)) {
      if (!this.isStructureVulnerable(target)) return false;
      const damage = Math.max(0, Math.round(amount));
      target.hp = Math.max(0, target.hp - damage);
      if (attacker) {
        attacker.damageDealt += damage;
        this.healFromDamage(attacker, damage);
      }
      this.events.push({ type: 'hit', sourceId, targetId: target.id, x: target.pos.x, z: target.pos.z, amount: damage, heavy });
      if (target.hp <= 0) this.destroyStructure(target);
      return true;
    }
    const damage = Math.max(0, Math.round(amount));
    target.hp = Math.max(0, target.hp - damage);
    if (attacker) {
      attacker.damageDealt += damage;
      this.healFromDamage(attacker, damage);
    }
    if (lengthSq(kbDir) > 0) target.vel = scale(normalize(kbDir), knockback * 0.6);
    this.events.push({ type: 'hit', sourceId, targetId: target.id, x: target.pos.x, z: target.pos.z, amount: damage, heavy });
    if (target.hp <= 0) {
      target.alive = false;
      this.events.push({ type: 'minionDeath', minionId: target.id, team: target.team, x: target.pos.x, z: target.pos.z });
      if (attacker) this.giveGold(attacker, GOLD.minion, target.pos);
      else this.awardMissedMinionGold(target);
      this.awardMinionXp(target);
    }
    return true;
  }

  /** Oro pasivo: un pequeño ingreso cada segundo de partida (sin número flotante). */
  private passiveGold(): void {
    const clock = this.match.enabled ? this.match.elapsedTicks : this.tick;
    if (clock <= 0 || clock % TICK_RATE !== 0) return;
    for (const hero of this.heroes) {
      if (!hero.isDummy) hero.gold += GOLD.passivePerSecond;
    }
  }

  private giveGold(hero: HeroState, amount: number, at: Vec2): void {
    hero.gold += amount;
    this.events.push({ type: 'gold', heroId: hero.id, amount, x: at.x, z: at.z });
  }

  /** Omnisucción: el atacante se cura una fracción del daño infligido. */
  private healFromDamage(attacker: HeroState, damage: number): void {
    if (damage <= 0 || !attacker.alive) return;
    const omnivamp = heroStats(attacker.heroId, attacker.level, attacker.items).omnivamp;
    if (omnivamp > 0) attacker.hp = Math.min(attacker.maxHp, attacker.hp + damage * omnivamp);
  }

  private destroyStructure(structure: StructureState): void {
    structure.alive = false;
    structure.hp = 0;
    structure.targetId = 0;
    this.events.push({
      type: 'structureDestroyed',
      structureId: structure.id,
      team: structure.team,
      kind: structure.kind,
      x: structure.pos.x,
      z: structure.pos.z,
    });
    if (structure.kind === 'tower') this.awardTowerGold(structure);
    if (structure.kind === 'nexus' && this.match.enabled && !this.match.ended) {
      this.endMatch(structure.team === 'blue' ? 'red' : 'blue');
    }
  }

  /** 250 de oro repartidos entre los héroes aliados dentro del rango de la torre al caer. */
  private awardTowerGold(structure: StructureState): void {
    const range = STRUCTURE_STATS.tower.attack.range;
    const allies = this.heroes.filter(
      (hero) =>
        hero.alive &&
        !hero.isDummy &&
        hero.team !== structure.team &&
        distance(hero.pos, structure.pos) <= range,
    );
    if (allies.length === 0) return;
    const amount = Math.round(GOLD.tower / allies.length);
    for (const hero of allies) this.giveGold(hero, amount, structure.pos);
  }

  private dealDamage(
    attacker: HeroState | undefined,
    target: HeroState,
    amount: number,
    kbDir: Vec2,
    knockback: number,
    hitstun: number,
    heavy: boolean,
    sourceId: number = attacker?.id ?? 0,
    reflected = false,
    /** daño puro (R de Link): ignora la armadura, pero sí puede reflejarse */
    ignoreArmor = false,
  ): boolean {
    if (target.invuln > 0) return false;

    const def = getHeroDef(target.heroId);
    const victimStats = heroStats(target.heroId, target.level, target.items);
    const attackerStats = attacker ? heroStats(attacker.heroId, attacker.level, attacker.items) : undefined;
    const armor = attackerStats
      ? effectiveArmor(victimStats.armor, attackerStats.armorPen, attackerStats.lethality)
      : victimStats.armor;
    const incoming = Math.max(0, Math.round(reflected || ignoreArmor ? amount : amount * armorMultiplier(armor)));
    // el escudo (W de Bowser) absorbe primero
    const absorbed = Math.min(target.shieldHp, incoming);
    target.shieldHp -= absorbed;
    const damage = incoming - absorbed;
    target.hp = Math.max(0, target.hp - damage);
    if (damage > 0) {
      target.lastDamagedAt = this.tick;
      this.cancelRecall(target);
    }
    if (attacker) {
      attacker.damageDealt += damage;
      this.healFromDamage(attacker, damage);
      if (attacker.team !== target.team) {
        target.lastEnemyHeroId = attacker.id;
        target.lastEnemyHeroAt = this.tick;
        const cutoff = this.tick - KILL_CREDIT_TICKS;
        target.assistContributors = target.assistContributors.filter((entry) => entry.at >= cutoff);
        const contribution = target.assistContributors.find((entry) => entry.heroId === attacker.id);
        if (contribution) contribution.at = this.tick;
        else target.assistContributors.push({ heroId: attacker.id, at: this.tick });
        this.towerAggro(attacker, target);
        this.minionAggro(attacker, target);
      }
    }

    // agarrado (o agarrando): los empujones no lo sacan del agarre
    const held = target.grabbedBy !== 0 || this.isGrabbing(target);
    const kb = held ? 0 : knockback * (1 - def.knockbackResist);
    const dir = lengthSq(kbDir) > 0 ? normalize(kbDir) : fromAngle(attacker?.facing ?? 0);
    if (kb > 0) target.vel = scale(dir, kb);
    // imparable (p. ej. Fire Bird de Falco): los aturdimientos no cortan la habilidad
    const castStrike = target.action.kind === 'cast' ? this.strikeFor(target, target.action) : null;
    if (hitstun > 0 && !castStrike?.unstoppable && !this.isGrabbing(target)) {
      const stun = controlDuration(hitstun, victimStats.tenacity);
      if (stun > 0) {
        target.hitstun = Math.max(target.hitstun, stun);
        target.action = {
          kind: 'hit',
          ticks: 0,
          duration: stun,
          chain: 0,
          hitIds: [],
        };
        target.actionSerial++;
      }
    }
    target.flashTicks = 4;

    this.events.push({
      type: 'hit',
      sourceId,
      targetId: target.id,
      x: target.pos.x,
      z: target.pos.z,
      amount: damage,
      heavy,
    });

    if (target.hp <= 0) this.kill(target, sourceId);
    else if (!reflected) this.reflectThorns(target, sourceId, incoming);
    return true;
  }

  /**
   * Escudo de espinas (W de Bowser): devuelve una fracción del daño recibido
   * al héroe o minion enemigo que lo causó. Las estructuras no reciben espinas
   * (las habilidades nunca dañan estructuras).
   */
  private reflectThorns(target: HeroState, sourceId: number, damage: number): void {
    if (target.thornsTicks <= 0 || damage <= 0) return;
    const source = this.unitById(sourceId);
    if (!source || !source.alive || source.team === target.team || isStructureUnit(source)) return;
    const amount = damage * target.thornsReflect;
    if (amount < 0.5) return;
    this.damageUnit(target, target.id, source, amount, vec2(), 0, 0, false, true);
  }

  private botInput(hero: HeroState): InputState {
    const input = emptyInput();
    // canalizando el regreso: quedarse quieto
    if (hero.recallTicks > 0) return input;
    const retreat = this.laneEnabled && this.shouldRetreat(hero);
    if (retreat && this.shouldRecall(hero)) {
      input.recall = true;
      input.aimX = hero.pos.x + Math.sin(hero.facing);
      input.aimZ = hero.pos.z + Math.cos(hero.facing);
      return input;
    }
    const target = retreat ? undefined : this.botTarget(hero);
    if (!target) {
      const home = retreat || !this.laneEnabled ? hero.spawnPos : this.rallyPoint(hero);
      const dx = home.x - hero.pos.x;
      const dz = home.z - hero.pos.z;
      const dist = Math.hypot(dx, dz);
      if (dist > 1.4) {
        input.moveX = dx / dist;
        input.moveZ = dz / dist;
        input.aimX = home.x;
        input.aimZ = home.z;
      } else {
        input.aimX = hero.pos.x + Math.sin(hero.facing);
        input.aimZ = hero.pos.z + Math.cos(hero.facing);
      }
      return input;
    }

    input.aimX = target.pos.x;
    input.aimZ = target.pos.z;

    const dx = target.pos.x - hero.pos.x;
    const dz = target.pos.z - hero.pos.z;
    const dist = Math.hypot(dx, dz);
    const dirX = dist > 1e-5 ? dx / dist : 0;
    const dirZ = dist > 1e-5 ? dz / dist : 1;
    const def = getHeroDef(hero.heroId);
    const basic = def.attacks[0];
    const range = basic.range * heroStats(hero.heroId, hero.level, hero.items).attackRange;
    const preferred = range + target.radius - 0.4;
    const tooFar = dist > preferred + 0.45;
    const tooClose = dist < preferred - 0.9;
    const ranged = hero.heroId === 'mage' || Boolean(def.ranged);

    let moveX = 0;
    let moveZ = 0;
    if (tooFar) {
      moveX = dirX;
      moveZ = dirZ;
    } else if (ranged && tooClose) {
      moveX = -dirX;
      moveZ = -dirZ;
    } else {
      const strafe = (this.tick + hero.id * 17) % 120 < 60 ? 1 : -1;
      moveX = -dirZ * 0.45 * strafe;
      moveZ = dirX * 0.45 * strafe;
    }
    input.moveX = moveX;
    input.moveZ = moveZ;

    if (hero.hitstun > 0) return input;

    const canAct = hero.action.kind === 'idle' || this.canStartAbility(hero);
    if (!canAct) return input;

    const decision = (this.tick + hero.id * 7) % 5 === 0;
    const phase = (this.tick + hero.id * 23) % 100;
    const inBasic = dist <= range + target.radius - 0.15;
    const inQ = hero.ranks.q > 0 && dist <= this.abilityReach(def.abilities.q) + target.radius;
    const inW = hero.ranks.w > 0 && dist <= this.abilityReach(def.abilities.w) + target.radius;
    // el agarre sólo sirve contra héroes
    const inR =
      hero.ranks.r > 0 &&
      dist <= this.abilityReach(def.abilities.r) + target.radius &&
      (def.abilities.r.kind !== 'grab' || isHeroUnit(target)) &&
      // la ejecución sólo contra héroes y para rematar (o casi)
      (def.abilities.r.kind !== 'execute' ||
        (isHeroUnit(target) && this.executeDamage(hero, target, def.abilities.r.execute) >= target.hp * 0.8));

    if (decision) {
      if (inQ && hero.cooldowns.q <= 0 && phase < 55) {
        input.abilities.q = true;
        return input;
      }
      if (inW && hero.cooldowns.w <= 0 && phase < 40) {
        input.abilities.w = true;
        return input;
      }
      if (inR && hero.cooldowns.r <= 0 && phase < 28) {
        input.abilities.r = true;
        return input;
      }
      const inE = hero.ranks.e > 0 && dist <= this.abilityReach(def.abilities.e) + target.radius;
      if (inE && hero.cooldowns.e <= 0 && phase < 45) {
        input.abilities.e = true;
        return input;
      }
    }

    if (inBasic) {
      if (hero.action.kind === 'idle') {
        input.attack = true;
      } else if (
        hero.action.kind === 'attack' &&
        hero.action.ticks >= hero.action.duration * 0.45 &&
        hero.action.chain < 2
      ) {
        input.attack = true;
      }
    }

    return input;
  }

  /** Alcance útil de una habilidad (IA de los bots y apuntado táctil). */
  abilityReach(ability: AbilityDef): number {
    if (ability.kind === 'strike') {
      // las habilidades con carga llegan más lejos que su radio de golpe
      const dash = ability.strike.dashTicks * ability.strike.dashSpeed * TICK_DT;
      return ability.strike.range + dash * 0.6;
    }
    if (ability.kind === 'projectile') {
      return Math.min(16, ability.projectile.speed * ability.projectile.ttl * TICK_DT * 0.8);
    }
    if (ability.kind === 'shield') return 4; // se protege cuando el rival ya está encima
    if (ability.kind === 'grab') return ability.grab.dashTicks * ability.grab.dashSpeed * TICK_DT * 0.6 + ability.grab.reach + 1;
    if (ability.kind === 'execute') return ability.execute.range;
    return ability.blink.distance;
  }

  /** Retirarse a curarse con poca vida; quedarse en el nexo hasta recuperarse. */
  private shouldRetreat(hero: HeroState): boolean {
    if (hero.hp < hero.maxHp * 0.25) return true;
    const nexus = this.structures.find((s) => s.kind === 'nexus' && s.team === hero.team && s.alive);
    return Boolean(nexus && hero.hp < hero.maxHp * 0.9 && distance(nexus.pos, hero.pos) < NEXUS_HEAL_RADIUS + 1);
  }

  /** Volver a base si está lejos del nexo y no hay héroes enemigos cerca. */
  private shouldRecall(hero: HeroState): boolean {
    if (hero.action.kind !== 'idle' || hero.hitstun > 0) return false;
    if (distance(hero.pos, hero.spawnPos) < 15) return false;
    return !this.heroes.some((o) => o.alive && o.team !== hero.team && distance(o.pos, hero.pos) < 10);
  }

  /** Torre enemiga (para `team`) que cubre `pos` y no tiene minions aliados de `team` tanqueándola. */
  private untankedTowerAt(pos: Vec2, team: Team): StructureState | undefined {
    return this.structures.find((tower) => {
      if (!tower.alive || tower.team === team) return false;
      const range = STRUCTURE_STATS[tower.kind].attack.range;
      return (
        distance(tower.pos, pos) <= range + 1 &&
        !this.minions.some((m) => m.alive && m.team === team && this.inRange(tower.pos, m, range))
      );
    });
  }

  private botTarget(hero: HeroState): Unit | undefined {
    if (!this.laneEnabled) return this.nearestEnemy(hero);
    const safe = (unit: Unit) => !this.untankedTowerAt(unit.pos, hero.team);

    let focus: HeroState | undefined;
    for (const other of this.heroes) {
      if (!other.alive || other.team === hero.team || !safe(other)) continue;
      if (distance(hero.pos, other.pos) > 12) continue;
      if (!focus || other.hp < focus.hp) focus = other;
    }
    if (focus) return focus;

    let best: Unit | undefined;
    let bestDist = Infinity;
    for (const unit of this.enemyUnits(hero.team, true)) {
      if (isHeroUnit(unit)) continue;
      if (isMinionUnit(unit) && (!safe(unit) || distance(hero.pos, unit.pos) > 11)) continue;
      if (isStructureUnit(unit) && this.untankedTowerAt(unit.pos, hero.team)) continue;
      const d = distanceSq(hero.pos, unit.pos);
      if (d < bestDist) {
        best = unit;
        bestDist = d;
      }
    }
    return best;
  }

  /** Progreso de un minion a lo largo de su línea (mayor = más adelantado). */
  private minionProgress(minion: MinionState): number {
    const path = laneWaypoints(minion.lane, minion.team);
    const next = path[Math.min(minion.waypoint, path.length - 1)];
    return minion.waypoint * 1000 - distance(minion.pos, next);
  }

  /**
   * Sin objetivo: seguir al minion aliado más adelantado de su línea, o esperar
   * junto a la torre propia más avanzada de esa línea.
   */
  private rallyPoint(hero: HeroState): Vec2 {
    const lane = hero.lane ?? 1;
    let front: MinionState | undefined;
    for (const minion of this.minions) {
      if (!minion.alive || minion.team !== hero.team || minion.lane !== lane) continue;
      if (!front || this.minionProgress(minion) > this.minionProgress(front)) front = minion;
    }
    const path = laneWaypoints(lane, hero.team);
    if (front) {
      const back = path[Math.max(0, front.waypoint - 1)];
      const dir = normalize(sub(back, front.pos));
      return add(front.pos, scale(dir, 2.5));
    }
    let tower: StructureState | undefined;
    for (const s of this.structures) {
      if (!s.alive || s.team !== hero.team || s.kind !== 'tower' || s.lane !== lane) continue;
      if (!tower || s.tier < tower.tier) tower = s;
    }
    if (tower) return vec2(tower.pos.x, tower.pos.z);
    return hero.spawnPos;
  }

  private nearestEnemy(hero: HeroState): HeroState | undefined {
    let nearest: HeroState | undefined;
    let best = Infinity;
    let focus: HeroState | undefined;
    let focusHp = Infinity;
    for (const other of this.heroes) {
      if (!other.alive || other.team === hero.team) continue;
      const dist = distanceSq(hero.pos, other.pos);
      if (dist < best) {
        best = dist;
        nearest = other;
      }
      if (dist < 100 && other.hp < focusHp) {
        focus = other;
        focusHp = other.hp;
      }
    }
    return focus ?? nearest;
  }

  private checkMatchEnd(): void {
    if (!this.match.enabled || this.match.ended) return;
    const { blue, red } = this.match.score;
    if (this.match.scoreToWin > 0 && blue >= this.match.scoreToWin) {
      this.endMatch('blue');
      return;
    }
    if (this.match.scoreToWin > 0 && red >= this.match.scoreToWin) {
      this.endMatch('red');
      return;
    }
    if (this.match.durationTicks > 0 && this.match.elapsedTicks >= this.match.durationTicks && blue !== red) {
      this.endMatch(blue > red ? 'blue' : 'red');
    }
  }

  private endMatch(winner: Team): void {
    this.match.ended = true;
    this.match.winner = winner;
    this.events.push({
      type: 'matchEnd',
      winner,
      blue: this.match.score.blue,
      red: this.match.score.red,
    });
  }

  private updateDummy(hero: HeroState): void {
    const player = this.player;
    if (!player || !player.alive) return;
    if (hero.action.kind === 'idle') {
      hero.facing = angleTo(hero.pos, player.pos);
    }
    if (
      hero.heroId === 'mage' &&
      hero.action.kind === 'idle' &&
      this.tick % 150 === 60 &&
      distance(hero.pos, player.pos) < 16
    ) {
      this.spawnProjectile(
        hero.id,
        'orb',
        hero.pos.x,
        hero.pos.z,
        angleTo(hero.pos, player.pos),
        13,
        14,
        0.35,
        100,
        3,
        5,
      );
    }
  }

  private applyMovement(hero: HeroState, input: InputState | undefined): void {
    if (!hero.alive) return;

    if (lengthSq(hero.vel) > 0.0001) {
      hero.pos = add(hero.pos, scale(hero.vel, TICK_DT));
      hero.vel = scale(hero.vel, KNOCKBACK_FRICTION);
      if (length(hero.vel) < KNOCKBACK_MIN_SPEED) hero.vel = vec2();
    }

    if (hero.hitstun > 0) return;

    const action = hero.action;
    const castStrike = action.kind === 'cast' ? this.strikeFor(hero, action) : null;
    const castLocked = action.kind === 'cast' && !(castStrike && !castStrike.moveLock);
    if (!input) return;
    const dir = normalize(vec2(input.moveX, input.moveZ));
    if (action.kind === 'attack' && lengthSq(dir) > 0 && !input.attack) {
      // moverse cancela el autoataque: tras el golpe, sin perder nada; durante la
      // preparación (sólo jugadores con orden de movimiento, sin objetivo fijado), el golpe no sale y se recupera el intervalo
      if (this.attackHitDone(hero, action)) {
        hero.action = idleAction();
      } else if (!hero.isBot && hero.targetId === 0) {
        hero.action = idleAction();
        hero.attackCooldown = 0;
        hero.chainWindow = 0;
      }
    }
    const locked = castLocked || hero.action.kind === 'attack';
    if (locked) return;

    if (lengthSq(dir) > 0) {
      hero.pos = add(hero.pos, scale(dir, heroStats(hero.heroId, hero.level, hero.items).moveSpeed * TICK_DT));
      // andando mira hacia donde va (salvo que persiga a su objetivo: ya lo mira)
      if (hero.action.kind === 'idle' && !this.validTarget(hero, hero.targetId)) hero.facing = angleOf(dir);
    }
  }

  private updateProjectiles(): void {
    for (const projectile of this.projectiles) {
      if (!projectile.alive) continue;

      let homingTarget: Unit | undefined;
      if (projectile.homingId) {
        homingTarget = this.unitById(projectile.homingId);
        if (!homingTarget || !homingTarget.alive) {
          projectile.alive = false;
          continue;
        }
        const delta = sub(homingTarget.pos, projectile.pos);
        const len = length(delta);
        const speed = length(projectile.vel) || TOWER.projectileSpeed;
        if (len > 1e-5) projectile.vel = scale(delta, speed / len);
      }

      projectile.prevPos = cloneVec2(projectile.pos);
      projectile.pos = add(projectile.pos, scale(projectile.vel, TICK_DT));
      projectile.ttl--;

      if (projectile.ttl <= 0 || length(projectile.pos) > ARENA_RADIUS) {
        if (projectile.ttl <= 0 && projectile.explodeOnExpire && projectile.explodeRadius > 0) {
          this.explodeProjectile(projectile);
        }
        projectile.alive = false;
        continue;
      }

      // sin daño de contacto ni explosión al impactar (la bomba): atraviesa unidades
      const passThrough = !homingTarget && projectile.damage <= 0 && !projectile.explodeOnHit;
      const candidates = passThrough ? [] : homingTarget ? [homingTarget] : this.enemyUnits(projectile.team);
      for (const unit of candidates) {
        if (distance(unit.pos, projectile.pos) > unit.radius + projectile.radius) continue;
        if (projectile.hitIds.includes(unit.id)) continue;
        if (isHeroUnit(unit) && this.isReflecting(unit)) {
          this.reflectProjectile(projectile, unit);
          break;
        }
        if (isHeroUnit(unit) && unit.invuln > 0) continue;

        projectile.hitIds.push(unit.id);
        this.applyProjectileHit(projectile, unit);

        if (projectile.explodeOnHit && projectile.explodeRadius > 0) {
          this.explodeProjectile(projectile);
        }

        if (projectile.pierce > 0 && !homingTarget) {
          projectile.pierce--;
        } else {
          projectile.alive = false;
        }
        break;
      }
    }
    this.projectiles = this.projectiles.filter((projectile) => projectile.alive);
  }

  /** ¿Tiene el escudo reflector activo (habilidad con `reflectProjectiles`)? */
  private isReflecting(hero: HeroState): boolean {
    if (!hero.alive || hero.action.kind !== 'cast') return false;
    return Boolean(this.strikeFor(hero, hero.action)?.reflectProjectiles);
  }

  /**
   * Rebota un proyectil enemigo: pasa al equipo del que refleja (que se lleva el
   * crédito del daño). Los normales vuelven por donde vinieron; los teledirigidos
   * persiguen a quien los lanzó; los de estructuras se absorben.
   */
  private reflectProjectile(projectile: ProjectileState, hero: HeroState): void {
    const source = this.unitById(projectile.ownerId);
    projectile.team = hero.team;
    projectile.ownerId = hero.id;
    projectile.reflected = true;
    projectile.hitIds = [hero.id];
    projectile.ttl = Math.max(projectile.ttl, 45);
    if (projectile.homingId) {
      if (source && source.alive && !isStructureUnit(source)) projectile.homingId = source.id;
      else projectile.alive = false;
    } else {
      projectile.vel = scale(projectile.vel, -1);
      const away = normalize(sub(projectile.pos, hero.pos));
      projectile.pos = add(hero.pos, scale(away, hero.radius + projectile.radius + 0.1));
    }
    this.events.push({ type: 'reflect', heroId: hero.id, x: projectile.pos.x, z: projectile.pos.z });
  }

  private explodeProjectile(projectile: ProjectileState): void {
    this.events.push({
      type: 'projectileExplode',
      projectileId: projectile.id,
      x: projectile.pos.x,
      z: projectile.pos.z,
      kind: projectile.kind,
      radius: projectile.explodeRadius,
    });
    const owner = this.heroes.find((candidate) => candidate.id === projectile.ownerId);
    for (const unit of this.enemyUnits(projectile.team)) {
      if (distance(unit.pos, projectile.pos) > projectile.explodeRadius + unit.radius) continue;
      if (isHeroUnit(unit) && unit.invuln > 0) continue;
      const dir = normalize(sub(unit.pos, projectile.pos));
      this.damageUnit(
        owner,
        projectile.ownerId,
        unit,
        projectile.explodeDamage,
        dir,
        projectile.explodeKnockback,
        projectile.explodeHitstun,
        true,
      );
    }
  }

  private applyProjectileHit(projectile: ProjectileState, unit: Unit): void {
    const owner = this.heroes.find((candidate) => candidate.id === projectile.ownerId);
    const dir = normalize(projectile.vel);
    this.damageUnit(
      owner,
      projectile.ownerId,
      unit,
      projectile.damage,
      dir,
      projectile.knockback,
      projectile.hitstun,
      false,
    );

    this.events.push({
      type: 'projectileHit',
      projectileId: projectile.id,
      x: unit.pos.x,
      z: unit.pos.z,
      targetId: unit.id,
    });
  }

  // --------------------------------------------------------------------------
  // Línea: torres, minions, oleadas

  private healAtNexus(): void {
    for (const hero of this.heroes) {
      if (!hero.alive || hero.hp >= hero.maxHp) continue;
      const nexus = this.structures.find((s) => s.kind === 'nexus' && s.team === hero.team && s.alive);
      if (!nexus || distance(nexus.pos, hero.pos) > NEXUS_HEAL_RADIUS) continue;
      hero.hp = Math.min(hero.maxHp, hero.hp + hero.maxHp * NEXUS_HEAL_PER_SECOND * TICK_DT);
    }
  }

  private spawnWaves(): void {
    if (this.tick < this.nextWaveTick) return;
    this.nextWaveTick += MINION_WAVE.everyTicks;
    for (const team of ['blue', 'red'] as Team[]) {
      for (const lane of this.activeLanes) {
        const path = laneWaypoints(lane, team);
        for (let i = 0; i < MINION_WAVE.size; i++) {
          const row = Math.floor(i / 2);
          const col = i % 2;
          const p = pointAlong(path, MINION_WAVE.spawnDistance - row * MINION_WAVE.spacing);
          const lateral = (col - 0.5) * MINION_WAVE.spacing * 1.4;
          this.addMinion('koopa', team, p.x - p.dirZ * lateral, p.z + p.dirX * lateral, lane);
        }
      }
    }
  }

  private inRange(from: Vec2, unit: Unit, range: number): boolean {
    return distance(from, unit.pos) <= range + unit.radius;
  }

  private updateStructures(): void {
    for (const tower of this.structures) {
      if (!tower.alive) continue;
      const attack = STRUCTURE_STATS[tower.kind].attack;
      if (tower.cooldown > 0) tower.cooldown--;

      let target = this.unitById(tower.targetId);
      const valid =
        target &&
        target.alive &&
        target.team !== tower.team &&
        !isStructureUnit(target) &&
        this.inRange(tower.pos, target, attack.range);
      if (!valid) target = this.towerPick(tower, attack.range);
      tower.targetId = target?.id ?? 0;
      if (!target) continue;

      tower.facing = angleTo(tower.pos, target.pos);
      if (tower.cooldown > 0) continue;
      tower.cooldown = attack.cooldownTicks;
      this.fireTowerShot(tower, target);
    }
  }

  /** Prioridad de torre: minions enemigos, luego héroes (el más cercano). */
  private towerPick(tower: StructureState, range: number): Unit | undefined {
    let best: Unit | undefined;
    let bestDist = Infinity;
    for (const minion of this.minions) {
      if (!minion.alive || minion.team === tower.team || !this.inRange(tower.pos, minion, range)) continue;
      const d = distanceSq(tower.pos, minion.pos);
      if (d < bestDist) {
        best = minion;
        bestDist = d;
      }
    }
    if (best) return best;
    for (const hero of this.heroes) {
      if (!hero.alive || hero.team === tower.team || !this.inRange(tower.pos, hero, range)) continue;
      const d = distanceSq(tower.pos, hero.pos);
      if (d < bestDist) {
        best = hero;
        bestDist = d;
      }
    }
    return best;
  }

  private fireTowerShot(tower: StructureState, target: Unit): void {
    const angle = angleTo(tower.pos, target.pos);
    const start = add(tower.pos, fromAngle(angle, tower.radius));
    const projectile: ProjectileState = {
      id: this.nextProjectileId++,
      ownerId: tower.id,
      team: tower.team,
      kind: 'tower',
      pos: cloneVec2(start),
      prevPos: cloneVec2(start),
      vel: fromAngle(angle, TOWER.projectileSpeed),
      radius: TOWER.projectileRadius,
      damage: isHeroUnit(target)
        ? STRUCTURE_STATS[tower.kind].attack.damageVsHero
        : STRUCTURE_STATS[tower.kind].attack.damageVsMinion,
      knockback: isHeroUnit(target) ? 2 : 0,
      hitstun: 0, // las torres no aturden
      ttl: TOWER.projectileTtl,
      alive: true,
      reflected: false,
      pierce: 0,
      hitIds: [],
      explodeRadius: 0,
      explodeDamage: 0,
      explodeKnockback: 0,
      explodeHitstun: 0,
      explodeOnExpire: false,
      explodeOnHit: false,
      homingId: target.id,
    };
    this.projectiles.push(projectile);
    this.events.push({ type: 'projectileSpawn', projectileId: projectile.id, x: start.x, z: start.z, kind: 'tower' });
  }

  /** Un héroe que ataca a otro bajo una torre enemiga se convierte en su objetivo. */
  private towerAggro(attacker: HeroState, victim: HeroState): void {
    for (const tower of this.structures) {
      if (!tower.alive || tower.team !== victim.team) continue;
      const range = STRUCTURE_STATS[tower.kind].attack.range;
      if (!this.inRange(tower.pos, attacker, range)) continue;
      if (!this.inRange(tower.pos, victim, range + 2)) continue;
      tower.targetId = attacker.id;
    }
  }

  private updateMinions(): void {
    for (const minion of this.minions) {
      if (!minion.alive) continue;
      const stats = MINION_STATS[minion.kind];
      minion.prevPos = cloneVec2(minion.pos);
      if (minion.cooldown > 0) minion.cooldown--;
      minion.attackTicks++;

      if (lengthSq(minion.vel) > 0.0001) {
        minion.pos = add(minion.pos, scale(minion.vel, TICK_DT));
        minion.vel = scale(minion.vel, KNOCKBACK_FRICTION);
        if (length(minion.vel) < KNOCKBACK_MIN_SPEED) minion.vel = vec2();
      }

      let target = this.unitById(minion.targetId);
      // los héroes se pierden al salir del área (correa corta); el resto con más margen
      const leash = target && isHeroUnit(target) ? stats.aggroRange * 1.15 : stats.aggroRange * 1.6;
      const valid =
        target &&
        target.alive &&
        target.team !== minion.team &&
        !(isStructureUnit(target) && !this.isStructureVulnerable(target)) &&
        this.inRange(minion.pos, target, leash);
      if (!valid && target && isHeroUnit(target) && target.alive) minion.returning = true;
      if (!valid) target = this.minionPick(minion, stats.aggroRange);
      minion.targetId = target?.id ?? 0;
      if (target) minion.returning = false;

      if (target) {
        const gap = distance(minion.pos, target.pos) - target.radius - minion.radius;
        minion.facing = angleTo(minion.pos, target.pos);
        if (gap > stats.range) {
          minion.pos = add(minion.pos, scale(fromAngle(minion.facing), minion.speed * TICK_DT));
        } else if (minion.cooldown <= 0) {
          const damage = isStructureUnit(target) ? stats.structureDamage : stats.damage;
          const dir = normalize(sub(target.pos, minion.pos));
          this.damageUnit(undefined, minion.id, target, damage, dir, isMinionUnit(target) ? 1.2 : 0, 0, false);
          minion.cooldown = stats.cooldownTicks;
          minion.attackTicks = 0;
        }
        continue;
      }

      // perdió a un héroe que huyó: ir a la estructura enemiga más cercana de su línea
      if (minion.returning) {
        const objective = this.laneObjective(minion);
        if (objective && !this.inRange(minion.pos, objective, stats.aggroRange)) {
          const toward = normalize(sub(objective.pos, minion.pos));
          minion.facing = angleOf(toward);
          minion.pos = add(minion.pos, scale(toward, minion.speed * TICK_DT));
          continue;
        }
        minion.returning = false;
        minion.waypoint = this.resyncWaypoint(minion);
      }

      // sin objetivo: seguir el recorrido de su línea hacia el nexo enemigo
      const path = laneWaypoints(minion.lane, minion.team);
      let next = path[Math.min(minion.waypoint, path.length - 1)];
      if (minion.waypoint < path.length - 1 && distance(minion.pos, next) < 1.5) {
        minion.waypoint++;
        next = path[minion.waypoint];
      }
      const dir = normalize(sub(next, minion.pos));
      if (lengthSq(dir) > 0) {
        minion.facing = angleOf(dir);
        minion.pos = add(minion.pos, scale(dir, minion.speed * TICK_DT));
      }
    }
  }

  /**
   * Próxima estructura enemiga de la línea del minion: torre exterior, interior,
   * la torre del nexo más cercana y por último el nexo.
   */
  private laneObjective(minion: MinionState): StructureState | undefined {
    const enemy = this.structures.filter((s) => s.alive && s.team !== minion.team);
    const laneTowers = enemy
      .filter((s) => s.kind === 'tower' && s.lane === minion.lane)
      .sort((a, b) => a.tier - b.tier);
    if (laneTowers.length) return laneTowers[0];
    const nexusTowers = enemy.filter((s) => s.kind === 'tower' && s.tier === 3);
    if (nexusTowers.length) {
      return nexusTowers.reduce((best, s) => (distanceSq(s.pos, minion.pos) < distanceSq(best.pos, minion.pos) ? s : best));
    }
    return enemy.find((s) => s.kind === 'nexus');
  }

  /** Índice del próximo punto del recorrido según la posición actual (tramo más cercano). */
  private resyncWaypoint(minion: MinionState): number {
    const path = laneWaypoints(minion.lane, minion.team);
    let best = 1;
    let bestDist = Infinity;
    for (let i = 1; i < path.length; i++) {
      const a = path[i - 1];
      const b = path[i];
      const abx = b.x - a.x;
      const abz = b.z - a.z;
      const t = Math.max(0, Math.min(1, ((minion.pos.x - a.x) * abx + (minion.pos.z - a.z) * abz) / (abx * abx + abz * abz)));
      const d = Math.hypot(minion.pos.x - (a.x + abx * t), minion.pos.z - (a.z + abz * t));
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return Math.max(minion.waypoint, best);
  }

  /**
   * Llamado de ayuda: un héroe que ataca a un héroe aliado de los minions estando
   * dentro de su área pasa a ser su objetivo (salvo que estén atacando una estructura).
   */
  private minionAggro(attacker: HeroState, victim: HeroState): void {
    for (const minion of this.minions) {
      if (!minion.alive || minion.team !== victim.team) continue;
      if (!this.inRange(minion.pos, attacker, MINION_STATS[minion.kind].aggroRange)) continue;
      const current = this.unitById(minion.targetId);
      if (current && current.alive && isStructureUnit(current)) continue;
      minion.targetId = attacker.id;
      minion.returning = false;
    }
  }

  /** Prioridad de minion: minions, estructuras atacables, héroes. */
  private minionPick(minion: MinionState, aggro: number): Unit | undefined {
    const pick = (units: Unit[]): Unit | undefined => {
      let best: Unit | undefined;
      let bestDist = Infinity;
      for (const unit of units) {
        if (!this.inRange(minion.pos, unit, aggro)) continue;
        const d = distanceSq(minion.pos, unit.pos);
        if (d < bestDist) {
          best = unit;
          bestDist = d;
        }
      }
      return best;
    };
    const enemies = this.enemyUnits(minion.team, true);
    return (
      pick(enemies.filter(isMinionUnit)) ??
      pick(enemies.filter(isStructureUnit)) ??
      pick(enemies.filter(isHeroUnit))
    );
  }

  private pushOutOfStructures(): void {
    for (const structure of this.structures) {
      if (!structure.alive) continue;
      const movers: Array<HeroState | MinionState> = [...this.heroes, ...this.minions];
      for (const unit of movers) {
        if (!unit.alive) continue;
        const delta = sub(unit.pos, structure.pos);
        const dist = length(delta);
        const min = structure.radius + unit.radius;
        if (dist >= min) continue;
        const dir = dist > 1e-5 ? scale(delta, 1 / dist) : vec2(1, 0);
        unit.pos = add(structure.pos, scale(dir, min));
      }
    }
  }

    private kill(hero: HeroState, killerId?: number): void {
    hero.hp = 0;
    hero.alive = false;
    hero.vel = vec2();
    hero.hitstun = 0;
    hero.action = {
      kind: 'dead',
      ticks: 0,
      duration: 0,
      chain: 0,
      hitIds: [],
    };
    hero.deaths++;
    hero.targetId = 0;
    hero.recallTicks = 0;
    hero.thornsTicks = 0;
    hero.shieldHp = 0;
    // suelta (o lo sueltan de) un agarre
    if (hero.grabbedBy !== 0) {
      const holder = this.heroes.find((other) => other.id === hero.grabbedBy);
      if (holder) this.releaseGrab(holder, hero);
    }
    for (const other of this.heroes) {
      if (other.grabbedBy === hero.id) this.releaseGrab(hero, other);
    }
    hero.grabbedBy = 0;
    hero.liftTicks = 0;
    hero.liftDuration = 0;
    hero.actionSerial++;
    // crece con el reloj de la partida (o con el tiempo de simulación fuera de partida)
    const elapsed = this.match.enabled ? this.match.elapsedTicks : this.tick;
    hero.respawnAtTick = this.tick + respawnTicksFor(elapsed);

    // la kill es del héroe enemigo que dio el golpe final o, si lo remató otra
    // cosa (torre, minion), del último héroe enemigo que lo dañó en los últimos 30 s
    const enemyHero = (id: number | undefined) =>
      id === undefined ? undefined : this.heroes.find((candidate) => candidate.id === id && candidate.team !== hero.team);
    const killer =
      enemyHero(killerId) ??
      (hero.lastEnemyHeroId && this.tick - hero.lastEnemyHeroAt <= KILL_CREDIT_TICKS
        ? enemyHero(hero.lastEnemyHeroId)
        : undefined);
    hero.lastEnemyHeroId = 0;
    if (killer) {
      this.giveGold(killer, GOLD.heroKill, hero.pos);
      this.gainXp(killer, XP.heroKillBase + XP.heroKillPerLevel * hero.level);
      if (this.match.enabled) {
        killer.kills++;
        this.match.score[killer.team]++;
      }
      const assistants = hero.assistContributors
        .filter((entry) => this.tick - entry.at <= KILL_CREDIT_TICKS)
        .map((entry) => enemyHero(entry.heroId))
        .filter((candidate): candidate is HeroState => Boolean(candidate));
      for (const assistant of assistants) {
        if (assistant.id === killer.id) continue;
        this.giveGold(assistant, GOLD.assist, hero.pos);
      }
    }
    hero.assistContributors = [];

    this.events.push({
      type: 'death',
      heroId: hero.id,
      killerId: killer?.id,
      x: hero.pos.x,
      z: hero.pos.z,
    });
  }

  private respawn(hero: HeroState): void {
    hero.alive = true;
    hero.hp = hero.maxHp;
    hero.pos = cloneVec2(hero.spawnPos);
    hero.prevPos = cloneVec2(hero.spawnPos);
    hero.vel = vec2();
    hero.facing = 0;
    hero.aim = vec2(hero.spawnPos.x, hero.spawnPos.z + 1);
    hero.action = idleAction();
    hero.cooldowns = emptyCooldowns();
    hero.charges = fullCharges(hero.heroId);
    hero.chargeTicks = emptyCooldowns();
    hero.hitstun = 0;
    hero.invuln = SPAWN_PROTECTION_TICKS;
    hero.chainWindow = 0;
    hero.attackCooldown = 0;
    hero.comboChain = 0;
    hero.lastEnemyHeroId = 0;
    hero.lastEnemyHeroAt = 0;
    hero.assistContributors = [];
    hero.flashTicks = 0;
    hero.lastDamagedAt = -OUT_OF_COMBAT_REGEN_TICKS;
    hero.targetId = 0;
    hero.recallTicks = 0;
    hero.thornsTicks = 0;
    hero.shieldHp = 0;
    hero.grabbedBy = 0;
    hero.liftTicks = 0;
    hero.liftDuration = 0;
    hero.actionSerial++;
    this.events.push({
      type: 'respawn',
      heroId: hero.id,
      x: hero.pos.x,
      z: hero.pos.z,
    });
  }

  private finishAction(hero: HeroState): void {
    hero.action = idleAction();
  }

  private separateHeroes(): void {
    const units: Array<HeroState | MinionState> = [
      ...this.heroes.filter((hero) => hero.alive),
      ...this.minions.filter((minion) => minion.alive),
    ];
    for (let i = 0; i < units.length; i++) {
      const a = units[i];
      for (let j = i + 1; j < units.length; j++) {
        const b = units[j];
        // héroes y minions se atraviesan; sólo se separan unidades del mismo tipo
        if (isHeroUnit(a) !== isHeroUnit(b)) continue;
        // Bowser y su agarrado van pegados
        if ('grabbedBy' in a && 'grabbedBy' in b && (a.grabbedBy === b.id || b.grabbedBy === a.id)) continue;
        const minDistance = a.radius + b.radius;
        const delta = sub(b.pos, a.pos);
        const dist = length(delta);
        if (dist >= minDistance) continue;
        if (dist < 1e-5) {
          a.pos = add(a.pos, vec2(-minDistance * 0.5, 0));
          b.pos = add(b.pos, vec2(minDistance * 0.5, 0));
          continue;
        }
        const push = (minDistance - dist) * 0.5;
        const dir = scale(delta, 1 / dist);
        a.pos = sub(a.pos, scale(dir, push));
        b.pos = add(b.pos, scale(dir, push));
      }
    }
  }

  private clampToArena(): void {
    const units: Array<HeroState | MinionState> = [...this.heroes, ...this.minions];
    for (const unit of units) {
      if (!unit.alive) continue;
      const maxRadius = ARENA_RADIUS - unit.radius;
      const dist = length(unit.pos);
      if (dist > maxRadius) {
        unit.pos = scale(normalize(unit.pos), maxRadius);
      }
    }
  }

    private facingFromAim(hero: HeroState): number {
    const delta = sub(hero.aim, hero.pos);
    if (lengthSq(delta) < 1e-6) return hero.facing;
    return angleOf(delta);
  }

  private knockbackDirection(origin: Vec2, target: Vec2, fallbackFacing: number): Vec2 {
    const delta = sub(target, origin);
    if (lengthSq(delta) < 1e-6) return fromAngle(fallbackFacing);
    return normalize(delta);
  }
}

export function createTrainingSimulation(playerHero: HeroId = 'knight'): Simulation {
  const sim = new Simulation();
  sim.addHero(playerHero, { team: 'blue', x: 0, z: -8, isPlayer: true, name: 'You' });
  const spots: Array<[number, number]> = [[7, 3], [-8, 5], [9, 10], [-3, 11], [3, 14]];
  const dummies = HERO_ORDER.filter((id) => id !== playerHero);
  dummies.forEach((heroId, i) => {
    const [x, z] = spots[i];
    sim.addHero(heroId, { team: 'red', x, z, isDummy: true, name: getHeroDef(heroId).displayName });
  });
  return sim;
}

export function heroDefOf(hero: HeroState): HeroDef {
  return getHeroDef(hero.heroId);
}
